// Next Step Navigation Component
// Provides consistent "Continue to Next Step" buttons across AIAG-VDA workflow pages
// ============================================================

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import clsx from 'clsx';

interface StepInfo {
    step: number;
    name: string;
    path: string;
}

const AIAG_VDA_STEPS: StepInfo[] = [
    { step: 1, name: 'Planning & Preparation', path: '/project-scoping' },
    { step: 2, name: 'Structure Analysis', path: '/process-flow' },
    { step: 3, name: 'Function Analysis', path: '/function-matrix' },
    { step: 4, name: 'Failure Analysis', path: '/failure-chains' },
    { step: 5, name: 'Risk Analysis', path: '/pfmea' },
    { step: 6, name: 'Optimization', path: '/actions' },
    { step: 7, name: 'Results Documentation', path: '/control-plan' },
];

interface NextStepButtonProps {
    currentStep: number;
    isComplete?: boolean;
    className?: string;
}

export const NextStepButton: React.FC<NextStepButtonProps> = ({
    currentStep,
    isComplete = false,
    className = '',
}) => {
    const navigate = useNavigate();
    const nextStep = AIAG_VDA_STEPS.find(s => s.step === currentStep + 1);

    if (!nextStep) return null;

    return (
        <button
            onClick={() => navigate(nextStep.path)}
            className={clsx(
                'flex items-center gap-2 px-4 py-2 rounded-lg transition-all',
                isComplete
                    ? 'bg-green-500 hover:bg-green-600 text-white'
                    : 'bg-primary-500 hover:bg-primary-600 text-white',
                className
            )}
        >
            {isComplete && <Check size={18} />}
            <span>Continue to Step {nextStep.step}: {nextStep.name}</span>
            <ArrowRight size={18} />
        </button>
    );
};

interface StepProgressIndicatorProps {
    currentStep: number;
    completedSteps?: number[];
    className?: string;
}

export const StepProgressIndicator: React.FC<StepProgressIndicatorProps> = ({
    currentStep,
    completedSteps = [],
    className = '',
}) => {
    return (
        <div className={clsx('flex items-center gap-1', className)}>
            {AIAG_VDA_STEPS.map((step, index) => {
                const isCompleted = completedSteps.includes(step.step);
                const isCurrent = step.step === currentStep;

                return (
                    <React.Fragment key={step.step}>
                        <div
                            className={clsx(
                                'w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all',
                                isCurrent && 'bg-primary-500 text-white ring-2 ring-primary-300',
                                isCompleted && !isCurrent && 'bg-green-500 text-white',
                                !isCurrent && !isCompleted && 'bg-slate-200 dark:bg-slate-700 text-slate-500'
                            )}
                            title={step.name}
                        >
                            {isCompleted && !isCurrent ? <Check size={16} /> : step.step}
                        </div>
                        {index < AIAG_VDA_STEPS.length - 1 && (
                            <div
                                className={clsx(
                                    'w-4 h-0.5',
                                    isCompleted ? 'bg-green-500' : 'bg-slate-200 dark:bg-slate-700'
                                )}
                            />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
};

interface WorkflowBreadcrumbProps {
    currentStep: number;
    currentPage: string;
    className?: string;
}

export const WorkflowBreadcrumb: React.FC<WorkflowBreadcrumbProps> = ({
    currentStep,
    currentPage,
    className = '',
}) => {
    const navigate = useNavigate();
    const stepInfo = AIAG_VDA_STEPS.find(s => s.step === currentStep);

    return (
        <nav className={clsx('flex items-center gap-2 text-sm', className)}>
            <button
                onClick={() => navigate('/')}
                className="text-slate-500 hover:text-primary-500 transition-colors"
            >
                Home
            </button>
            <span className="text-slate-400">/</span>
            <span className="text-slate-500">
                Step {currentStep}: {stepInfo?.name}
            </span>
            <span className="text-slate-400">/</span>
            <span className="text-primary-500 font-medium">{currentPage}</span>
        </nav>
    );
};

export { AIAG_VDA_STEPS };
export type { StepInfo };
